import React, { useState, useRef } from 'react';
import { useTranslation } from '../Context/TranslationContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Compass, Book, Target, Globe, Users, Star, 
  Flag, History, Award, Map, Play, Pause, 
  ChevronRight, X 
} from 'lucide-react';

const JamboreePage = () => {
  const { isTamil } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const [activeSection, setActiveSection] = useState('intro');

  const translations = {
    en: {
      sections: {
        introduction: "THE BHARAT SCOUTS & GUIDES",
        scouting: "What is Scouting – Science of Outing",
        principles: "Principles of Scouting",
        worldMovement: "World Scout Movement",
        indianMovement: "Scout Movement in India",
        tnMovement: "Scout Movement in Tamil Nadu",
        recentJamboree: "Recent Jamboree at National Level",
        goldenJubilee: "Golden Jubilee Jamboree - Tamil Nadu",
        diamondJubilee: "Diamond Jubilee Jamboree",
        mascot: "Jamboree Mascot",
        committees: "BSG Diamond Jubilee Jamboree Committees",
        gallery: "Event Gallery"
      },
      content: {
        scouting: {
          title: "Science of Outing",
          description: "It is a disciplined approach that aims at character building of youth, through which virtue, courage, patriotism, discipline, obedience, self reliance, leadership and discovering or rediscovering SELF through outing."
        },
        principles: [
          {
            title: "Duty to God",
            description: "Spiritual development and adherence to spiritual principles, fostering faith and understanding"
          },
          {
            title: "Duty to Others",
            description: "Loyalty to country, promoting local and global friendship, and serving the community"
          },
          {
            title: "Duty to Self",
            description: "Personal growth, continuous learning, and character development through scouting activities"
          }
        ],
        worldMovement: [
          {
            year: "1907",
            event: "The Scout movement for boys was started by Lord Baden Powell with an experimental camp at Brown Sea Island, Southern England for 21 boys."
          },
          {
            year: "1909",
            event: "A historic Rally was held with 11,000 Scouts at Crystal Palace in London, marking the movement's growth."
          },
          {
            year: "1910",
            event: "Lord Baden Powell initiated a parallel movement for girls, expanding scouting's reach."
          }
        ],
        indianMovement: [
          {
            year: "1909",
            event: "T.H.Baker established the first Scout Troop in Bangalore and registered with Imperial Headquarters in London."
          },
          {
            year: "1911",
            event: "The first Guide Company in India started in Jabalpur, Central India."
          },
          {
            year: "1950",
            event: "Various scouting associations merged to form The Bharat Scouts & Guides on November 7th."
          }
        ],
        tnMovement: [
          {
            year: "1913",
            event: "Ms. Annie Besant initiated the Scout Movement in Tamil Nadu."
          },
          {
            year: "1916",
            event: "Indian Boys Scout Association established at Gokalae Centre, Chennai on October 1st."
          },
          {
            year: "1917",
            event: "'The Boy Scouts of Madras' started at Adyar under Governor John Sinclair."
          },
          {
            year: "1927",
            event: "Mahatma Gandhi's historic visit to Tamil Nadu State Headquarters at Wenlock Park, Triplicane."
          }
        ],
        nationalJamboree: {
          title: "National Jamboree 2023 - Rohat Palli, Rajasthan",
          details: [
            "Budget allocation: Rs.35 Cr",
            "747 Tamil Nadu participants",
            "Adventure Park activities",
            "Fun Bases and Intellectual Activities",
            "Global Village Activities",
            "SDG's Hub Activities",
            "Tamil Nadu Day celebration",
            "Cultural performances and competitions"
          ],
          activities: [
            "Camp Craft",
            "Camp fire",
            "State Gateway",
            "Scout/Guide skills",
            "Pioneering Project",
            "Rangoli",
            "March past",
            "Physical Display",
            "Youth forum"
          ]
        },
        goldenJubilee: {
          title: "Golden Jubilee Jamboree 2000 - Chennai",
          theme: "GOLDEN PAST - BRIGHT FUTURE",
          highlights: [
            "15,000 participants from across India",
            "International participation from neighboring countries",
            "Chief Minister Dr.Kalaignar at Inaugural Ceremony",
            "Cultural exchange programs",
            "Skill development workshops"
          ]
        },
        mascot: {
          title: "The Nilgiri Tahr - Symbol of Strength",
          description: [
            "The Nilgiri tahr, Tamil Nadu's state animal, represents the spirit of scouting",
            "Native to the Western Ghats, symbolizing adventure and resilience",
            "Perfect embodiment of strength, adaptability, and environmental stewardship",
            "Represents the unity between scouts and nature"
          ]
        }
      }
    },
    ta: {
      sections: {
        introduction: "பாரத் சாரணர்கள் மற்றும் வழிகாட்டிகள்",
        scouting: "சாரணர் என்றால் என்ன - வெளிப்புற அறிவியல்",
        principles: "சாரணர் கொள்கைகள்",
        worldMovement: "உலக சாரணர் இயக்கம்",
        indianMovement: "இந்திய சாரணர் இயக்கம்",
        tnMovement: "தமிழ்நாடு சாரணர் இயக்கம்",
        recentJamboree: "சமீபத்திய தேசிய ஜாம்போரி",
        goldenJubilee: "தமிழ்நாடு பொன்விழா ஜாம்போரி",
        diamondJubilee: "வைர விழா ஜாம்போரி",
        mascot: "ஜாம்போரி சின்னம்",
        committees: "பி.எஸ்.ஜி வைர விழா ஜாம்போரி குழுக்கள்",
        gallery: "நிகழ்வு கேலரி"
      },
      content: {
        scouting: {
          title: "வெளிப்புற அறிவியல்",
          description: "இது இளைஞர்களின் குணாதிசய வளர்ச்சிக்கான ஒரு ஒழுங்குமுறை அணுகுமுறையாகும், இதன் மூலம் நல்லொழுக்கம், தைரியம், தேசப்பற்று, கட்டுப்பாடு, கீழ்ப்படிதல், சுய சார்பு, தலைமைத்துவம் மற்றும் வெளிப்புற செயல்பாடுகள் மூலம் சுயத்தை கண்டறிதல் அல்லது மறுகண்டுபிடித்தல்."
        },
        principles: [
          {
            title: "இறைவனுக்கான கடமை",
            description: "ஆன்மீக வளர்ச்சி மற்றும் ஆன்மீக கொள்கைகளை கடைப்பிடித்தல், நம்பிக்கை மற்றும் புரிதலை வளர்த்தல்"
          },
          {
            title: "பிறருக்கான கடமை",
            description: "நாட்டிற்கு விசுவாசம், உள்ளூர் மற்றும் உலகளாவிய நட்புறவை ஊக்குவித்தல், சமூகத்திற்கு சேவை செய்தல்"
          },
          {
            title: "சுய கடமை",
            description: "தனிப்பட்ட வளர்ச்சி, தொடர்ந்த கற்றல், மற்றும் சாரணர் செயல்பாடுகள் மூலம் குணாதிசய வளர்ச்சி"
          }
        ],
        worldMovement: [
          {
            year: "1907",
            event: "லார்ட் பேடன் பவெல் ஆல் தெற்கு இங்கிலாந்தின் பிரவுன் சீ தீவில் 21 சிறுவர்களுடன் பரிசோதனை முகாமுடன் சிறுவர்களுக்கான சாரணர் இயக்கம் தொடங்கப்பட்டது."
          },
          {
            year: "1909",
            event: "லண்டனின் கிரிஸ்டல் பேலஸில் 11,000 சாரணர்களுடன் வரலாற்று முக்கியத்துவம் வாய்ந்த பேரணி நடைபெற்றது, இயக்கத்தின் வளர்ச்சியை குறிக்கிறது."
          },
          {
            year: "1910",
            event: "லார்ட் பேடன் பவெல் பெண்களுக்கான இணை இயக்கத்தை தொடங்கி, சாரணர் இயக்கத்தின் வீச்சை விரிவுபடுத்தினார்."
          }
        ],
        indianMovement: [
          {
            year: "1909",
            event: "டி.எச்.பேக்கர் பெங்களூரில் முதல் சாரணர் குழுவை நிறுவி லண்டனில் உள்ள இம்பீரியல் தலைமையகத்தில் பதிவு செய்தார்."
          },
          {
            year: "1911",
            event: "மத்திய இந்தியாவின் ஜபல்பூரில் இந்தியாவின் முதல் வழிகாட்டி நிறுவனம் தொடங்கப்பட்டது."
          },
          {
            year: "1950",
            event: "நவம்பர் 7 ஆம் தேதி பல்வேறு சாரணர் சங்கங்கள் இணைந்து பாரத சாரணர் மற்றும் வழிகாட்டிகள் உருவாக்கப்பட்டது."
          }
        ],
        tnMovement: [
          {
            year: "1913",
            event: "திருமதி அன்னி பெசண்ட் தமிழ்நாட்டில் சாரணர் இயக்கத்தை தொடங்கினார்."
          },
          {
            year: "1916",
            event: "அக்டோபர் 1 ஆம் தேதி சென்னை கோகலே மையத்தில் இந்திய சிறுவர் சாரணர் சங்கம் நிறுவப்பட்டது."
          },
          {
            year: "1917",
            event: "ஆளுநர் ஜான் சின்க்ளேர் தலைமையில் அடையாரில் 'மெட்ராஸ் சாரணர்கள்' தொடங்கப்பட்டது."
          },
          {
            year: "1927",
            event: "திருவல்லிக்கேணியில் உள்ள வென்லாக் பூங்காவில் உள்ள தமிழ்நாடு மாநில தலைமையகத்திற்கு மகாத்மா காந்தியின் வரலாற்று முக்கியத்துவம் வாய்ந்த வருகை."
          }
        ],
        nationalJamboree: {
          title: "தேசிய ஜாம்போரி 2023 - ரோஹத் பள்ளி, ராஜஸ்தான்",
          details: [
            "பட்ஜெட் ஒதுக்கீடு: ரூ.35 கோடி",
            "தமிழ்நாட்டில் இருந்து 747 பங்கேற்பாளர்கள்",
            "சாகச பூங்கா செயல்பாடுகள்",
            "மகிழ்ச்சி மற்றும் அறிவுசார் செயல்பாடுகள்",
            "உலக கிராம செயல்பாடுகள்",
            "நிலையான வளர்ச்சி இலக்குகள் மையம் செயல்பாடுகள்",
            "தமிழ்நாடு தின கொண்டாட்டம்",
            "கலாச்சார நிகழ்ச்சிகள் மற்றும் போட்டிகள்"
          ],
          activities: [
            "முகாம் கலை",
            "முகாம் தீ",
            "மாநில நுழைவாயில்",
            "சாரணர்/வழிகாட்டி திறன்கள்",
            "முன்னோடி திட்டம்",
            "ரங்கோலி",
            "அணிவகுப்பு",
            "உடற்பயிற்சி காட்சி",
            "இளைஞர் மன்றம்"
          ]
        },
        goldenJubilee: {
          title: "பொன்விழா ஜாம்போரி 2000 - சென்னை",
          theme: "பொற்கால கடந்தகாலம் - ஒளிமயமான எதிர்காலம்",
          highlights: [
            "இந்தியா முழுவதும் இருந்து 15,000 பங்கேற்பாளர்கள்",
            "அண்டை நாடுகளில் இருந்து சர்வதேச பங்கேற்பு",
            "தொடக்க விழாவில் முதலமைச்சர் டாக்டர் கலைஞர்",
            "கலாச்சார பரிமாற்ற நிகழ்ச்சிகள்",
            "திறன் மேம்பாட்டு பட்டறைகள்"
          ]
        },
        mascot: {
          title: "நீலகிரி வரையாடு - வலிமையின் சின்னம்",
          description: [
            "தமிழ்நாட்டின் மாநில விலங்கான நீலகிரி வரையாடு, சாரணர் உணர்வை பிரதிநிதித்துவப்படுத்துகிறது",
            "மேற்குத் தொடர்ச்சி மலையின் சொந்த உயிரினம், சாகசம் மற்றும் மீள்திறனை குறிக்கிறது",
            "வலிமை, தகவமைப்பு மற்றும் சுற்றுச்சூழல் பாதுகாப்பின் சிறந்த வடிவமைப்பு",
            "சாரணர்கள் மற்றும் இயற்கைக்கு இடையேயான ஒற்றுமையை பிரதிநிதித்துவப்படுத்துகிறது"
          ]
        }
      }
    }
  };

  const t = translations[isTamil ? 'ta' : 'en'];
  const images = [
    { src: "/Images/jamboree1.png", caption: "" },
    { src: "/Images/jamboree2.png", caption: "" },
    { src: "/Images/jamboree3.png", caption: "" },
    { src: "/Images/jamboree4.png", caption: "" },
    { src: "/Images/jamboree5.png", caption: "" },
    { src: "/Images/jamboree6.png", caption: "" },
    { src: "/Images/jamboree7.png", caption: "" },
    { src: "/Images/jamboree8.png", caption: "" },
    { src: "/Images/jamboree9.png", caption: "" },
    { src: "/Images/jamboree10.png", caption: "" },
    { src: "/Images/jamboree11.png", caption: "" },
    { src: "/Images/jamboree12.png", caption: "" },
    { src: "/Images/jamboree13.png", caption: "" },
    { src: "/Images/jamboree14.png", caption: "" },
    { src: "/Images/jamboree15.png", caption: "" }
  ];
  const toggleVideo = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsVideoPlaying(true);
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF8] mt-16">
      <section className="pt-20 pb-8">
        <div className="max-w-5xl mx-auto px-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl font-bold mb-6 text-[#2C251D]">
              {t.sections.introduction}
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t.content.scouting.description}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-2 py-8">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.content.principles.map((principle, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-10 h-10 bg-[#8A7968] bg-opacity-10 rounded-full flex items-center justify-center mb-4">
                  <Target className="w-5 h-5 text-[#8A7968]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2C251D] mb-3">
                  {principle.title}
                </h3>
                <p className="text-gray-700">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#8A7968] bg-opacity-10 rounded-full flex items-center justify-center">
                <Globe className="w-5 h-5 text-[#8A7968]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2C251D]">
                {t.sections.worldMovement}
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-px bg-[#8A7968] bg-opacity-20"></div>
              <div className="space-y-8">
                {t.content.worldMovement.map((item, index) => (
                  <div key={index} className="relative pl-6">
                    <div className="absolute left-0 top-3 w-2.5 h-2.5 bg-[#8A7968] rounded-full transform -translate-x-1/2"></div>
                    <div className="bg-[#8A7968] bg-opacity-5 rounded-lg p-4">
                      <div className="text-base font-semibold text-[#8A7968] mb-2">
                        {item.year}
                      </div>
                      <p className="text-gray-700">
                        {item.event}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-[#8A7968] bg-opacity-5 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow">
                <Map className="w-5 h-5 text-[#8A7968]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2C251D]">
                {t.sections.indianMovement}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.content.indianMovement.map((item, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="text-lg font-semibold text-[#8A7968] mb-2">
                    {item.year}
                  </div>
                  <p className="text-gray-700">
                    {item.event}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[#8A7968] bg-opacity-10 rounded-full flex items-center justify-center">
                <Flag className="w-5 h-5 text-[#8A7968]" />
              </div>
              <h2 className="text-2xl font-bold text-[#2C251D]">
                {t.sections.tnMovement}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {t.content.tnMovement.map((item, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#8A7968] bg-opacity-5 rounded-lg flex items-center justify-center">
                    <span className="text-lg font-bold text-[#8A7968]">{item.year}</span>
                  </div>
                  <div>
                    <p className="text-gray-700">
                      {item.event}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-gradient-to-r from-[#8A7968] to-[#726354] rounded-xl p-6 text-white">
            <h2 className="text-2xl font-bold mb-8">
              {t.sections.recentJamboree}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold">
                  {t.content.nationalJamboree.title}
                </h3>
                <div className="space-y-3">
                  {t.content.nationalJamboree.details.map((detail, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-white rounded-full mt-2"></div>
                      <p className="text-white/90">{detail}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-6">Activities</h3>
                <div className="grid grid-cols-2 gap-3">
                  {t.content.nationalJamboree.activities.map((activity, index) => (
                    <div key={index} className="flex items-center gap-2 bg-white/10 rounded-lg p-3">
                      <Star className="w-4 h-4 text-white" />
                      <p className="text-white/90 text-sm">{activity}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
            <h2 className="text-2xl font-bold text-[#2C251D] mb-6">
              {t.sections.goldenJubilee}
            </h2>
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-[#2C251D] mb-3">
                  {t.content.goldenJubilee.title}
                </h3>
                <p className="text-lg text-[#8A7968] font-semibold">
                  {t.content.goldenJubilee.theme}
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {t.content.goldenJubilee.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-center gap-3 bg-[#8A7968] bg-opacity-5 rounded-lg p-4">
                    <Award className="w-5 h-5 text-[#8A7968] flex-shrink-0" />
                    <p className="text-gray-700 text-sm">{highlight}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-[#8A7968] bg-opacity-5 rounded-xl p-6">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow">
                  <Award className="w-5 h-5 text-[#8A7968]" />
                </div>
                <h2 className="text-2xl font-bold text-[#2C251D]">
                  {t.sections.mascot}
                </h2>
              </div>
              <h3 className="text-xl font-semibold text-[#2C251D] mb-4">
                {t.content.mascot.title}
              </h3>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="aspect-square bg-[#8A7968] bg-opacity-5 rounded-lg p-4 flex items-center justify-center">
                    <img 
                      src="/Images/mascot1.png" 
                      alt="Nilgiri Tahr Mascot"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="space-y-3">
                    {t.content.mascot.description.map((desc, index) => (
                      <p key={index} className="text-gray-700">{desc}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="rounded-xl overflow-hidden">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#2C251D]">
                {t.sections.gallery}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {images.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="group relative cursor-pointer aspect-square"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="absolute inset-0 rounded-lg overflow-hidden">
                    <img
                      src={image.src}
                      alt={image.caption}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="absolute bottom-3 left-3 right-3 text-white text-sm">
                        {image.caption}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>
        
       {/* Video Section moved to the end */}
       <section className="pb-12">
          <div className="max-w-4xl mx-auto px-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="relative aspect-video rounded-xl overflow-hidden shadow-lg"
            >
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                controls
                playsInline
              >
                <source src="/videos/jamboree.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </motion.div>
          </div>
        </section>

        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-3"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden"
              >
                <img
                  src={selectedImage.src}
                  alt={selectedImage.caption}
                  className="w-full h-auto"
                />
                <div className="p-3 bg-white">
                  <p className="text-[#2C251D] text-base font-medium">
                    {selectedImage.caption}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImage(null);
                  }}
                  className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <X className="w-4 h-4 text-[#2C251D]" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default JamboreePage;